import {
  addTime,
  adjustFromTimeZone,
  ContainerDNUStatisticsSnapshotEntity,
  ContainerDNUStatisticsSnapshotEntryEntity,
  containsValue,
  getDefaultTimeFrame,
  getTimeFrameDates,
  transportMethod,
  TransportMethod,
  isBefore,
  startOf,
  TransportCategory
} from "@ots-frontend/ots-data";
import {useMemo} from "react";
import {getDateFormatterOptions,} from "../../chart/TimeChart/TimeChart";
import {useTranslation} from "react-i18next";
import {LineChart, LineChartBucketData, LineChartData, LineChartProps} from "../../chart/LineChart/LineChart";
import {Point} from "chart.js";

export type ContainerDNUStatisticsByTimeChartProps = Pick<LineChartProps, 'id'> & {
  fromDttm: number
  toDttm?: number
  snapshots?: ContainerDNUStatisticsSnapshotEntity[]
  category?: TransportCategory
  methods?: TransportMethod[]
  splitMethods?: boolean
}

const getCount = (methods: TransportMethod[], entry: ContainerDNUStatisticsSnapshotEntryEntity): number => {
  let count = 0

  if (containsValue(methods, transportMethod.trucking)) {
    count += entry.count;
  }

  if (containsValue(methods, transportMethod.pipeline)) {
    count += entry.pipelineCount;
  }

  return count
}

const getTotal = (methods: TransportMethod[], entry: ContainerDNUStatisticsSnapshotEntryEntity): number => {
  let total = 0

  if (containsValue(methods, transportMethod.trucking)) {
    total += entry.total;
  }

  if (containsValue(methods, transportMethod.pipeline)) {
    total += entry.pipelineTotal;
  }

  return total
}

const getValue = (methods: TransportMethod[], entry: ContainerDNUStatisticsSnapshotEntryEntity): number => {
  const count = getCount(methods, entry)
  const total = getTotal(methods, entry)
  return total - count
}

export const ContainerDNUStatisticsByTimeChart = (props: ContainerDNUStatisticsByTimeChartProps) => {
  const {
    id,
    fromDttm,
    toDttm = new Date().getTime(),
    snapshots,
    category,
    methods = [transportMethod.trucking, transportMethod.pipeline],
    splitMethods = true,
  } = props

  const {t} = useTranslation()

  const timeFrame = useMemo(() => {
    const defaultTimeFrame = getDefaultTimeFrame(fromDttm, toDttm)
    if (defaultTimeFrame === 'MONTH' || defaultTimeFrame === 'WEEK') {
      return "DAY"
    }
    return defaultTimeFrame
  }, [fromDttm, toDttm])

  const chartData: LineChartData = useMemo(() => {
    if (snapshots) {
      const dates: string[] = []
      const buckets: Record<string, LineChartBucketData> = {}

      const timeFrameDates = getTimeFrameDates(timeFrame, fromDttm, toDttm)

      let startDttm = timeFrameDates.startDttm
      const endDttm = timeFrameDates.endDttm

      const formatter = new Intl.DateTimeFormat(undefined, getDateFormatterOptions(timeFrame))

      while (isBefore(startDttm, endDttm)) {
        dates.push(formatter.format(startDttm))

        snapshots.forEach((snapshot) => {
          if (!category || category.cargoSubtype === snapshot.cargoSubtype) {
            const objectName = snapshot.objectName ?? "Unknown"
            const entry = snapshot.entries.find((entry) => startOf(timeFrame, adjustFromTimeZone(entry.dttm)).getTime() === startDttm.getTime())

            if (splitMethods) {
              for (const method of methods) {
                let value: number | Point | null = null
                if (entry) {
                  value = getValue([method], entry)
                }
                const bucketName = objectName + " - " + t(method.displayValue.id, method.displayValue.defaultMessage)
                if (bucketName in buckets) {
                  buckets[bucketName].values.push(value)
                } else {
                  buckets[bucketName] = {
                    values: [value]
                  }
                }
              }
            } else {
              let value: number | Point | null = null
              if (entry) {
                value = getValue(methods, entry)
              }

              if (objectName in buckets) {
                buckets[objectName].values.push(value)
              } else {
                buckets[objectName] = {
                  values: [value]
                }
              }
            }
          }
        })

        startDttm = addTime(timeFrame, startDttm, 1)
      }

      return {
        titles: dates,
        buckets: buckets,
      }
    }

    return {
      titles: [],
      buckets: {}
    }
  }, [snapshots, timeFrame, fromDttm, toDttm, category, splitMethods, methods, t])

  return (
    <LineChart
      id={id}
      stacked={{
        enabled: true,
        filled: true,
      }}
      xAxisTitle={t<string>("common.insight.containerdnubytime.xaxis.title", "Time")}
      yAxisTitle={t<string>("common.insight.containerdnubytime.yaxis.title", "# of Containers")}
      data={chartData}/>
  )
}
